import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { DETAIL_TABLE_WIDER_DATES_HINT } from '@/components/common/detailTableLayout.constants';
import { useLocalListSearch } from '@/hooks/useLocalListSearch';
import { useRememberViewedRow, useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { getZmHandOutStatCards } from '@/utils/handOutStatCards';
import { getTableEmptyText } from '@/utils/listSearch';
import { promoMembersQueryOptions } from '../api/promotion.api';
import { PROMO_ADJUSTMENT_UNIT_DISPLAY } from '../constants/promotion.constants';
import { usePromoAdjustmentNavigation } from '../hooks/usePromoAdjustmentNavigation';
import type {
  PromoAdjustmentUnit,
  PromoDrillParams,
  PromoDrillSearchBox,
  PromoMember,
} from '../types/promotion.types';
import { getPromoMemberColumns } from './PromoColumns';

const toSearchableZmValues = (zm: PromoMember) => [zm.name, zm.hrEmpId, zm.userMobile];

interface PromoItemZmsViewProps {
  unit: PromoAdjustmentUnit;
  promoItemId: number;
  zmSearch: PromoDrillParams;
  searchBox: PromoDrillSearchBox;
  title: string | undefined;
}

export function PromoItemZmsView({
  unit,
  promoItemId,
  zmSearch,
  searchBox,
  title,
}: PromoItemZmsViewProps) {
  const { openAdjustmentList } = usePromoAdjustmentNavigation(unit);
  useRememberViewedRow('promoItems', promoItemId);
  const lastViewedZmId = useRevealLastViewedRow('promoItemZms', promoItemId);

  const unitDisplay = PROMO_ADJUSTMENT_UNIT_DISPLAY[unit];
  const zmsQuery = useQuery(promoMembersQueryOptions(promoItemId, zmSearch));

  const { searchText, setSearchText, visibleRows } = useLocalListSearch({
    rows: zmsQuery.data,
    appliedText: searchBox.appliedText,
    onApply: searchBox.onApply,
    toSearchableValues: toSearchableZmValues,
  });

  const appliedSearchText = searchBox.appliedText;
  const columns = useMemo(
    () => getPromoMemberColumns(unit, promoItemId, zmSearch, appliedSearchText),
    [unit, promoItemId, zmSearch, appliedSearchText],
  );

  return (
    <DetailTableLayout
      title={title}
      titleAddon={<DateRangeBadge dateFrom={zmSearch.dateFrom} dateTo={zmSearch.dateTo} />}
      search={{
        value: searchText,
        onChange: setSearchText,
        placeholder: 'ZM name, HR ID or mobile',
      }}
      onNoHistory={() => openAdjustmentList(zmSearch)}
      stats={getZmHandOutStatCards(zmsQuery.data, unitDisplay.amountUnit)}
      table={{
        rows: visibleRows,
        columns,
        isLoading: zmsQuery.isLoading,
        error: zmsQuery.error,
        errorTitle: 'Could not load the ZMs',
        onRetry: () => void zmsQuery.refetch(),
        stickyColumnId: 'name',
        ...getTableEmptyText(
          appliedSearchText,
          'Nobody handed this item out in the period',
          DETAIL_TABLE_WIDER_DATES_HINT,
        ),
        isRowHighlighted: (zm) => zm.userId === lastViewedZmId,
      }}
    />
  );
}
